import React, {
  useReducer,
  useState,
  useCallback,
  useRef,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  TextField,
} from '@mui/material';
import { DropResult } from 'react-beautiful-dnd';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import {
  QuestionChoiceWithText,
  ChoiceWithTextPayload,
  ChoiceWithTextPayloadOptions,
  QuestionTypeV2,
} from '../../../redux/types';
import SortableList from '../../layout/SortableList';

export type Props = {
  surveyId: number;
  sectionId?: number;
  question?: QuestionChoiceWithText;
  addQuestion?: (dq: Omit<QuestionChoiceWithText, 'id'>) => void;
  updateQuestion?: (dq: QuestionChoiceWithText) => void;
  onCancelEdit?: () => void;
}

type PayloadItem = ChoiceWithTextPayloadOptions;

type SortOption = PayloadItem & { name: string; };

type questionActions = { type: 'updateText'; payload: string } | { type: 'multiSelect'; payload: boolean } | {type: 'addOption'; payload: string} |
{ type: 'updateOption'; payload:ChoiceWithTextPayloadOptions } | { type: 'deleteOption', payload: ChoiceWithTextPayloadOptions; } |
{ type: 'resetState'} | { type: 'reorderOptions', payload: ChoiceWithTextPayloadOptions[] };

const initialState: { text: string, payload?: ChoiceWithTextPayload } = {
  text: '',
  payload: undefined,
};

const reducer = (state: typeof initialState, action: questionActions): typeof initialState => {
  const initialPayload: ChoiceWithTextPayload = {
    multi: false,
    options: [],
  };
  switch (action.type) {
    case 'resetState':
      return initialState;
    case 'updateText':
      return {
        ...state,
        text: action.payload,
      };
    case 'multiSelect':
      return {
        ...state,
        payload: {
          ...state.payload || initialPayload,
          multi: action.payload,
        },
      };
    case 'addOption': {
      const newPayload: ChoiceWithTextPayload = state.payload
        ? { ...state.payload } : initialPayload;
      const { options } = newPayload;
      options.push({
        id: Date.now(),
        text: action.payload,
        textBox: false,
      });
      return {
        ...state,
        payload: newPayload,
      };
    }
    case 'updateOption': {
      const newPayload: ChoiceWithTextPayload = state.payload
        ? { ...state.payload } : initialPayload;
      const { options } = newPayload;
      const updatedPayloadOpts = options.map((e) => {
        if (e.id === action.payload.id) {
          return {
            ...e,
            id: e.id,
            text: action.payload.text,
            textBox: action.payload.textBox,
            placeholder: action.payload.placeholder,
          };
        }
        return e;
      });
      return {
        ...state,
        payload: {
          ...newPayload,
          options: updatedPayloadOpts,
        },
      };
    }
    case 'deleteOption': {
      const newPayload: ChoiceWithTextPayload = state.payload
        ? { ...state.payload } : initialPayload;
      const { options } = newPayload;
      const filteredPayloadOpts = options.filter((e) => e.id !== action.payload.id);
      return {
        ...state,
        payload: {
          ...newPayload,
          options: filteredPayloadOpts,
        },
      };
    }
    case 'reorderOptions': {
      const newPayload: ChoiceWithTextPayload = state.payload
        ? { ...state.payload } : initialPayload;
      return {
        ...state,
        payload: {
          ...newPayload,
          options: action.payload,
        },
      };
    }
    default:
      return state;
  }
};

const ChoiceWithTextQuestionEditor: React.FC<Props> = ({
  surveyId,
  sectionId,
  question,
  addQuestion,
  updateQuestion,
  onCancelEdit,
}: Props): JSX.Element => {
  const [state, dispatch] = useReducer(reducer, question || initialState);
  const [optionText, setOptionText] = useState<string>('');
  const [sort, setSort] = useState<boolean>(false);
  const sortedListRef = useRef<SortOption[]>();
  const sortOptionList = state.payload?.options.map((e) => ({ ...e, name: e.text } as SortOption));

  const handleQuestionTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const questionText = event.target.value;
    dispatch({
      type: 'updateText',
      payload: questionText,
    });
  };

  const multiSelectChangeHandler = (e: React.SyntheticEvent<Element, Event>, checked: boolean) => {
    dispatch({
      type: 'multiSelect',
      payload: checked,
    });
  };

  const onChangeOptionHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const optiontxt = event.target.value;
    setOptionText(optiontxt);
  };

  const handleEditOption = (
    item: PayloadItem, text: string, enableText: boolean, label?: string,
  ) => {
    dispatch({
      type: 'updateOption',
      payload: {
        id: item.id,
        text,
        textBox: enableText,
        placeholder: label,
      },
    });
  };
  const handleAddOption = () => {
    dispatch({
      type: 'addOption',
      payload: optionText,
    });
    setOptionText('');
  };

  const onEnterAddOption = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleAddOption();
    }
  };

  const handleDeleteOption = (item: PayloadItem) => {
    dispatch({
      type: 'deleteOption',
      payload: item,
    });
  };

  const saveMultiQuestion = () => {
    if (question && updateQuestion) {
      const updatedQuestion = {
        ...question,
        text: state.text,
        payload: state.payload,
      };
      updateQuestion(updatedQuestion);
      if (onCancelEdit) {
        onCancelEdit();
      }
    } else if (!question && addQuestion) {
      const questionData = {
        digitalSurveyId: surveyId,
        digitalSectionId: sectionId,
        type: QuestionTypeV2.ChoiceWithText,
        text: state.text,
        payload: state.payload,
      } as Omit<QuestionChoiceWithText, 'id'>;
      addQuestion(questionData);
      dispatch({ type: 'resetState' });
    }
  };

  const cancelEdit = () => {
    if (onCancelEdit) {
      onCancelEdit();
    }
  };

  const sortHandler = () => {
    setSort(true);
  };

  const saveSortHandler = () => {
    setSort(false);
    if (sortedListRef.current) {
      dispatch({
        type: 'reorderOptions',
        payload: sortedListRef.current,
      });
    }
    sortedListRef.current = undefined;
  };

  const onDragHandler = useCallback((result: DropResult, sortedList?: SortOption[]) => {
    if (sortedList) {
      sortedListRef.current = sortedList;
    }
  }, []);

  return (
    <Box>
      <FormControlLabel onChange={multiSelectChangeHandler} checked={state.payload?.multi || false} control={<Checkbox />} label="Make multiple options selectable" />
      <TextField
        variant="filled"
        size="small"
        label="question text"
        value={state.text}
        onChange={handleQuestionTextChange}
        fullWidth
      />
      {
        !sort && (
          <Box aria-label="edit-container">
            {
              state.payload && (
                <Box>
                  {
                    state.payload.options.map((e, i) => (
                      <Box key={e.id} display="flex" alignItems="center" sx={{ pl: 2, my: 1 }}>
                        <Paper elevation={0} sx={{ flexGrow: 0.5, p: 2 }}>
                          <TextField
                            variant="standard"
                            size="small"
                            label={`Option ${i + 1}`}
                            value={e.text}
                            onChange={
                              (evt: React.ChangeEvent<HTMLInputElement>) => {
                                handleEditOption(e, evt.target.value, e.textBox, e.placeholder);
                              }
                            }
                            fullWidth
                          />
                        </Paper>
                        <Paper elevation={0} sx={{ flexGrow: 0.5 }}>
                          <FormGroup>
                            <FormControlLabel onChange={(evt, checked) => handleEditOption(e, e.text, checked, e.placeholder)} checked={e.textBox} control={<Checkbox />} label="Enable Text Input" componentsProps={{ typography: { variant: 'body2' } }} />
                            {
                              e.textBox && (
                                <TextField
                                  size="small"
                                  label="Enter text input label"
                                  value={e.placeholder || ''}
                                  onChange={
                                    (evt: React.ChangeEvent<HTMLInputElement>) => {
                                      handleEditOption(e, e.text, e.textBox, evt.target.value);
                                    }
                                  }
                                />
                              )
                            }
                          </FormGroup>
                        </Paper>
                        <IconButton
                          aria-label={`delete-option ${i + 1}`}
                          onClick={() => { handleDeleteOption(e); }}
                          size="large"
                        >
                          <ClearIcon />
                        </IconButton>
                      </Box>
                    ))
                  }
                </Box>
              )
            }
            {
              state.text && (
                <Box display="flex" sx={{ pl: 2 }}>
                  <TextField
                    variant="standard"
                    size="small"
                    label="Add Option"
                    value={optionText}
                    onChange={onChangeOptionHandler}
                    fullWidth
                    onKeyPress={onEnterAddOption}
                  />
                  <IconButton aria-label="add-option" onClick={handleAddOption} size="large">
                    <AddIcon />
                  </IconButton>
                </Box>
              )
            }
          </Box>
        )
      }
      {
          !!sort && sortOptionList && (
            <Box aria-label="sort-container">
              <SortableList<SortOption>
                droppableId="sortable-options"
                list={sortOptionList}
                onDragEndHandler={onDragHandler}
              />
            </Box>
          )
      }
      {
        state.payload && state.payload.options.length > 0 && !sort && (
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }} gap={1}>
            <Button
              aria-label="sort-options"
              onClick={sortHandler}
              variant="outlined"
              size="small"
            >
              Reorder
            </Button>
            <Button
              aria-label="save-question"
              onClick={saveMultiQuestion}
              variant="outlined"
              size="small"
            >
              Save
            </Button>
            {
              question && (
                <Button
                  aria-label="cancel"
                  color="secondary"
                  onClick={cancelEdit}
                  variant="outlined"
                  size="small"
                >
                  Cancel
                </Button>
              )
            }
          </Box>
        )
      }
      {
        sort && (
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }} gap={2}>
            <Button
              aria-label="sort-options"
              onClick={saveSortHandler}
            >
              Done
            </Button>
          </Box>
        )
      }
    </Box>
  );
};

export default ChoiceWithTextQuestionEditor;
